import { sendHTTPRequest } from '@/util/http'
import { toolkitUrls } from '@/util/links'
import { Goal } from './goal_types'
import type { StaircaseGradeGroup } from './staircase_types'
import type { AnchorPageContent, AnchorPageCustomization, AnchorPageRequestParams } from './anchor_page_types'

export async function getGoalByUUID(uuid: string, params: Record<string, string | number | boolean> = {}): Promise<Goal> {
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.api}/goal/${uuid}`,
      method: 'GET',
      urlParams: params as Record<string, string>,
      includeCredentials: true
    })
    return await response.json() as Goal
  } catch (e) {
    throw new Error('Error getting goal by UUID', { cause: e })
  }
}

export async function getStaircaseGoals(goalUuid: string, standardSetCode: string, subject: string): Promise<StaircaseGradeGroup[]> {
  if (!standardSetCode) return []
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.api}/goal/${goalUuid}/staircase/${standardSetCode}`,
      method: 'GET',
      urlParams: { group: '1', subject },
      includeCredentials: true
    })
    return await response.json() as StaircaseGradeGroup[]
  } catch (e) {
    throw new Error('Error getting staircase goals', { cause: e })
  }
}

function toUrlParams(params: object): Record<string, string> {
  const definedEntries = Object.entries(params).filter(([, value]) => value !== undefined)
  return Object.fromEntries(definedEntries.map(([key, value]) => [key, String(value)]))
}

/** Rethrows the HTTPError itself so the caller can tell a 403 (premium content) from any other failure. */
export async function getAnchorPage(uuid: string, params: AnchorPageRequestParams): Promise<AnchorPageContent> {
  const response = await sendHTTPRequest({
    url: `${toolkitUrls.api}/super-pages/${encodeURIComponent(uuid)}`,
    method: 'GET',
    urlParams: toUrlParams(params),
    includeCredentials: true
  })
  return await response.json() as AnchorPageContent
}

export async function getAnchorPageCustomization(
  uuid: string,
  contentProfile: string,
  isBetaMode: boolean
): Promise<AnchorPageCustomization> {
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.api}/super-pages/${encodeURIComponent(uuid)}/customization`,
      method: 'GET',
      urlParams: toUrlParams({ content_profile: contentProfile, beta_mode: isBetaMode }),
      includeCredentials: true
    })
    return await response.json() as AnchorPageCustomization
  } catch (e) {
    throw new Error('Failed to get anchor page customization', { cause: e })
  }
}

/** The content profiles with goal prompts a customization can run in the given mode. */
export async function getCustomizableGoalProfiles(isBetaMode: boolean): Promise<string[]> {
  const response = await sendHTTPRequest({
    url: `${toolkitUrls.api}/goal_customization/profiles`,
    method: 'GET',
    urlParams: toUrlParams({ beta_mode: isBetaMode }),
    includeCredentials: true
  })
  return await response.json() as string[]
}
